import React, { useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import placeEnhancementsService, { PlaceAccountLink, PlaceProForma } from "@/services/PlaceEnhancementsService"

interface PlaceProFormaTabProps {
  placeId: string
  placeName?: string
  placeModel?: any
}

const PlaceProFormaTab: React.FC<PlaceProFormaTabProps> = ({ placeId, placeName, placeModel }) => {
  const { t } = useTranslation("global")
  const [proformas, setProformas] = useState<PlaceProForma[]>(() => placeEnhancementsService.getProFormas(placeId))
  const [accountLink, setAccountLink] = useState<PlaceAccountLink | null>(() => placeEnhancementsService.getAccountLink(placeId))

  const [showProFormaModal, setShowProFormaModal] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState(false)

  // Account form
  const [customerName, setCustomerName] = useState(
    accountLink?.customerName || placeModel?.customer?.name || placeModel?.name || ""
  )
  const [accountNumber, setAccountNumber] = useState(
    accountLink?.accountNumber || placeModel?.creditCode || placeModel?.customerId || ""
  )

  // Proforma form
  const [code, setCode] = useState(() => "PF-" + Math.floor(1000 + Math.random() * 9000))
  const [amount, setAmount] = useState<number>(0)
  const [currency, setCurrency] = useState("DOP")
  const [status, setStatus] = useState<PlaceProForma["status"]>("draft")
  const [itemsCount, setItemsCount] = useState<number>(5)
  const [notes, setNotes] = useState("")

  // --- Technical Details Calculations ---
  const floors = Number(placeModel?.quantityOfFloor || 1)
  const aptsPerFloor = Number(placeModel?.departmentPerFloor || 1)
  const totalApts = Number(placeModel?.totalApartments || (floors * aptsPerFloor) || 1)
  const habitableArea = Number(placeModel?.totalHabitableArea || placeModel?.constructionM2 || 120)
  const nonHabitableArea = Number(placeModel?.totalNonHabitableArea || (habitableArea * 0.2))
  const totalM2 = Math.max(10, Math.round(habitableArea + nonHabitableArea))

  // Calculated materials estimation items based on technical values
  const calculatedItems = [
    {
      material: "Bloques de Hormigón (15 x 20 x 40 cm)",
      desc: `12.5 bloques / m² (${totalM2} m² total)`,
      qty: Math.round(totalM2 * 12.5),
      unitPrice: 42.00,
    },
    {
      material: "Cemento Gris Estructural (Fundas 42.5 kg)",
      desc: `0.45 fundas / m² (${totalM2} m² total)`,
      qty: Math.round(totalM2 * 0.45),
      unitPrice: 490.00,
    },
    {
      material: "Varilla de Acero 3/8\" Grado 60 (Quintales)",
      desc: `0.08 quintales / m² (${totalM2} m² total)`,
      qty: Math.round(totalM2 * 0.08 * 10) / 10,
      unitPrice: 3850.00,
    },
    {
      material: "Pintura Acrílica Profesional (Cuñetes 5 Gal)",
      desc: `Rendimiento ~70 m² / cuñete (${totalM2} m² total)`,
      qty: Math.max(1, Math.ceil(totalM2 / 70)),
      unitPrice: 4600.00,
    },
    {
      material: "Mortero Seco Mezclafácil (Fundas 50 lb)",
      desc: `0.6 fundas / m² (${totalM2} m² total)`,
      qty: Math.round(totalM2 * 0.6),
      unitPrice: 280.00,
    },
  ]

  const calculatedSubtotal = calculatedItems.reduce((acc, item) => acc + (item.qty * item.unitPrice), 0)
  const calculatedItbis = calculatedSubtotal * 0.18
  const calculatedTotal = calculatedSubtotal + calculatedItbis

  const reloadData = () => {
    setProformas(placeEnhancementsService.getProFormas(placeId))
    setAccountLink(placeEnhancementsService.getAccountLink(placeId))
  }

  const handleSaveAccount = (e: React.FormEvent) => {
    e.preventDefault()
    placeEnhancementsService.saveAccountLink(placeId, {
      customerName,
      accountNumber,
    })
    setShowAccountModal(false)
    reloadData()
  }

  const handleGenerateAutoProForma = () => {
    const newCode = "PF-" + Math.floor(1000 + Math.random() * 9000)
    placeEnhancementsService.addProForma({
      placeId,
      code: newCode,
      amount: Math.round(calculatedTotal),
      currency: "DOP",
      date: new Date().toISOString().substring(0, 10),
      status: "draft",
      itemsCount: calculatedItems.length,
      notes: `Generado automáticamente desde detalles técnicos de la obra (${totalM2} m² total, ${totalApts} aptos).`,
    })
    reloadData()
  }

  const handleSaveProForma = (e: React.FormEvent) => {
    e.preventDefault()
    if (!code.trim() || amount <= 0) return

    placeEnhancementsService.addProForma({
      placeId,
      code,
      amount,
      currency,
      date: new Date().toISOString().substring(0, 10),
      status,
      itemsCount,
      notes,
    })

    setShowProFormaModal(false)
    reloadData()
  }

  const handleDeleteProForma = (id: string) => {
    if (window.confirm(t("Are you sure you want to delete this pro-forma record?"))) {
      placeEnhancementsService.deleteProForma(id)
      reloadData()
    }
  }

  const getStatusBadge = (st: PlaceProForma["status"]) => {
    if (st === "approved") return <Badge bg="success">{t("Approved")}</Badge>
    if (st === "sent") return <Badge bg="primary">{t("Sent")}</Badge>
    if (st === "rejected") return <Badge bg="danger">{t("Rejected")}</Badge>
    return <Badge bg="secondary">{t("Draft")}</Badge>
  }

  return (
    <div className="py-2">
      {/* Clean & Simplified Linked Account Card */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold small text-uppercase">
            <i className="fas fa-building-user me-2 text-primary" />
            {t("Cuenta de Cliente Vinculada (ERP)")}
          </span>
          <Button variant="outline-primary" size="sm" className="py-0" onClick={() => setShowAccountModal(true)}>
            <i className="fas fa-pen me-1" />
            {accountLink ? t("Editar Cuenta") : t("Vincular Cuenta")}
          </Button>
        </Card.Header>
        <Card.Body className="small py-3">
          <Row className="g-3 align-items-center">
            <Col md={5}>
              <div className="text-muted small mb-1">{t("Código del Cliente / ERP")}</div>
              <div className="fw-bold fs-6 text-dark font-monospace">
                {accountLink?.accountNumber || placeModel?.creditCode || placeModel?.customerId || "Sin código vinculado"}
              </div>
            </Col>
            <Col md={7}>
              <div className="text-muted small mb-1">{t("Nombre del Cliente / Razón Social")}</div>
              <div className="fw-bold fs-6 text-dark">
                {accountLink?.customerName || placeModel?.customer?.name || placeModel?.name || "Sin nombre registrado"}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Auto-Calculated Material Estimation Table from Technical Details */}
      <Card className="border shadow-sm mb-4">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2 border-bottom">
          <div>
            <h6 className="fw-bold mb-0 text-dark">
              <i className="fas fa-calculator me-2 text-warning" />
              {t("Cálculos y Estimación de Materiales según Detalles Técnicos")}
            </h6>
            <small className="text-muted">
              Valores calculados automáticamente en base a {totalM2} m² totales ({totalApts} aptos, {floors} pisos)
            </small>
          </div>
          <Button variant="warning" size="sm" className="fw-semibold text-dark" onClick={handleGenerateAutoProForma}>
            <i className="fas fa-magic me-1" />
            {t("Generar Pro-Forma Automática")}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="align-middle small mb-0">
            <thead className="table-light">
              <tr>
                <th>MATERIAL / CONCEPTO</th>
                <th>DESCRIPCIÓN TÉCNICA</th>
                <th className="text-center">CANTIDAD</th>
                <th className="text-end">PRECIO UNIT. (RD$)</th>
                <th className="text-end pe-3">SUBTOTAL (RD$)</th>
              </tr>
            </thead>
            <tbody>
              {calculatedItems.map((item, idx) => {
                const itemSubtotal = item.qty * item.unitPrice
                return (
                  <tr key={idx}>
                    <td className="fw-semibold text-dark">{item.material}</td>
                    <td className="text-muted small">{item.desc}</td>
                    <td className="text-center fw-bold">{item.qty.toLocaleString()}</td>
                    <td className="text-end font-monospace">RD$ {item.unitPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="text-end pe-3 fw-bold font-monospace">RD$ {itemSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="table-light border-top">
              <tr>
                <td colSpan={4} className="text-end fw-semibold">Subtotal Materiales:</td>
                <td className="text-end pe-3 font-monospace">RD$ {calculatedSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-end fw-semibold">ITBIS / Impuestos Estimados (18%):</td>
                <td className="text-end pe-3 font-monospace text-muted">RD$ {calculatedItbis.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
              <tr className="table-warning">
                <td colSpan={4} className="text-end fw-bold fs-6">TOTAL ESTIMADO PRO-FORMA (RD$):</td>
                <td className="text-end pe-3 fw-bold fs-6 font-monospace text-dark">
                  RD$ {calculatedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      {/* Pro-Forma List & Quotation History */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="fw-bold mb-0">
            <i className="fas fa-file-invoice-dollar me-2 text-success" />
            {t("Historial de Pro-Formas y Cotizaciones")}
          </h6>
          <small className="text-muted">{t("Registro e historial de presupuestos emitidos")}</small>
        </div>
        <Button variant="success" size="sm" onClick={() => setShowProFormaModal(true)}>
          <i className="fas fa-plus me-1" />
          {t("Nueva Pro-Forma Manual")}
        </Button>
      </div>

      {proformas.length === 0 ? (
        <Alert variant="light" className="border text-center py-4">
          <i className="fas fa-receipt fa-2x mb-2 text-muted" />
          <div className="text-muted small">{t("No hay registros pro-forma emitidos para este lugar.")}</div>
          <Button variant="outline-success" size="sm" className="mt-2" onClick={() => setShowProFormaModal(true)}>
            <i className="fas fa-plus me-1" />
            {t("Crear Primera Pro-Forma")}
          </Button>
        </Alert>
      ) : (
        <Table hover responsive className="align-middle small mb-0 border">
          <thead className="table-light">
            <tr>
              <th>{t("Código / No.")}</th>
              <th>{t("Fecha")}</th>
              <th>{t("Ítems")}</th>
              <th>{t("Monto Total")}</th>
              <th>{t("Observaciones")}</th>
              <th>{t("Estado")}</th>
              <th className="text-end">{t("Acciones")}</th>
            </tr>
          </thead>
          <tbody>
            {proformas.map(p => (
              <tr key={p.id}>
                <td className="fw-bold text-primary">{p.code}</td>
                <td>{p.date}</td>
                <td>{p.itemsCount} {t("ítems")}</td>
                <td className="fw-bold font-monospace">
                  {p.currency} ${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="text-muted small">{p.notes || "—"}</td>
                <td>{getStatusBadge(p.status)}</td>
                <td className="text-end">
                  <Button variant="outline-danger" size="sm" className="px-2 py-0" onClick={() => handleDeleteProForma(p.id)}>
                    <i className="fas fa-trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Account Modal */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} centered>
        <Form onSubmit={handleSaveAccount}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold">{t("Vincular Cuenta de Cliente ERP")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="small">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Nombre del Cliente / Razón Social")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  placeholder={t("e.g. Ferretería Ochoa S.A.")}
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Código del Cliente / ERP No.")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  placeholder={t("e.g. CLI-8940")}
                  value={accountNumber}
                  onChange={e => setAccountNumber(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowAccountModal(false)}>
              {t("Cancelar")}
            </Button>
            <Button variant="primary" size="sm" type="submit">
              {t("Guardar Vinculación")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ProForma Modal */}
      <Modal show={showProFormaModal} onHide={() => setShowProFormaModal(false)} centered>
        <Form onSubmit={handleSaveProForma}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold">{t("Nueva Pro-Forma")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="small">
            <Row className="g-3">
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Código Pro-Forma")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  value={code}
                  onChange={e => setCode(e.target.value)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Estado")}</Form.Label>
                <Form.Select
                  size="sm"
                  value={status}
                  onChange={e => setStatus(e.target.value as any)}
                >
                  <option value="draft">{t("Borrador")}</option>
                  <option value="sent">{t("Enviado")}</option>
                  <option value="approved">{t("Aprobado")}</option>
                  <option value="rejected">{t("Rechazado")}</option>
                </Form.Select>
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Monto Total")} *</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount || ""}
                  onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Moneda")}</Form.Label>
                <Form.Select
                  size="sm"
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                >
                  <option value="DOP">DOP (RD$)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Observaciones / Detalles")}</Form.Label>
                <Form.Control
                  size="sm"
                  as="textarea"
                  rows={2}
                  placeholder={t("Resumen de materiales o servicios cotizados...")}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowProFormaModal(false)}>
              {t("Cancelar")}
            </Button>
            <Button variant="success" size="sm" type="submit">
              {t("Guardar Pro-Forma")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PlaceProFormaTab
